<!--
  svelte-virtual-list bd13f93c5a172780726456565a35b85badf252b8

  https://github.com/sveltejs/svelte-virtual-list/commit/bd13f93c5a172780726456565a35b85badf252b8

  Modified to add Typescript types and remove unused code.

  ==========

  Copyright (c) 2018 Rich Harris

  Permission is hereby granted by the authors of this software, to any person,
  to use the software for any purpose, free of charge, including the rights to
  run, read, copy, change, distribute and sell it, and including usage rights to
  any patents the authors may hold on it, subject to the following conditions:

  This license, or a link to its text, must be included with all copies of the
  software and any derivative works.

  Any modification to the software submitted to the authors may be incorporated
  into the software under the terms of this license.

  The software is provided "as is", without warranty of any kind, including but
  not limited to the warranties of title, fitness, merchantability and
  non-infringement. The authors have no obligation to provide support or updates
  for the software, and may not be held liable for any damages, claims or other
  liability arising from its use.
-->
<script lang="ts">
  import { onMount, tick } from 'svelte';

  type ItemType = $$Generic<{ id: string }>;

  // props
  export let items: ItemType[];
  export let height = '100%';

  // read-only, but visible to consumers via bind:start
  export let start = 0;
  export let end = 0;

  // local state
  const heightMap: number[] = [];
  let rows: HTMLCollectionOf<HTMLElement>;
  let viewport: HTMLElement;
  let contents: HTMLElement;
  let viewportHeight = 0;
  let visible: { index: number; data: ItemType }[];
  let mounted = false;

  let top = 0;
  let bottom = 0;
  let averageHeight: number;

  /* eslint-disable @typescript-eslint/no-unsafe-assignment */
  $: visible = items
    .slice(start, end)
    .map((data, i) => ({ index: i + start, data }));
  /* eslint-enable @typescript-eslint/no-unsafe-assignment */

  const refresh = async (_items: ItemType[], _viewportHeight: number) => {
    const { scrollTop } = viewport;

    await tick(); // wait until the DOM is up to date

    let contentHeight = top - scrollTop;
    let i = start;

    while (contentHeight < _viewportHeight && i < _items.length) {
      let row = rows[i - start];

      if (!row) {
        end = i + 1;
        // eslint-disable-next-line no-await-in-loop
        await tick(); // render the newly visible row
        row = rows[i - start];
      }

      const rowHeight = row.offsetHeight;
      heightMap[i] = row.offsetHeight;
      contentHeight += rowHeight;
      i += 1;
    }

    end = i;

    const remaining = _items.length - end;
    averageHeight = (top + contentHeight) / end;

    bottom = remaining * averageHeight;
    heightMap.length = _items.length;
  };

  // whenever `items` changes, invalidate the current heightmap
  $: if (mounted) void refresh(items, viewportHeight);

  const handleScroll = async () => {
    const { scrollTop } = viewport;

    const oldStart = start;

    for (let v = 0; v < rows.length; v += 1) {
      heightMap[start + v] = rows[v].offsetHeight;
    }

    let i = 0;
    let y = 0;

    while (i < items.length) {
      const rowHeight = heightMap[i] || averageHeight;
      if (y + rowHeight > scrollTop) {
        start = i;
        top = y;

        break;
      }

      y += rowHeight;
      i += 1;
    }

    while (i < items.length) {
      y += heightMap[i] || averageHeight;
      i += 1;

      if (y > scrollTop + viewportHeight) break;
    }

    end = i;

    const remaining = items.length - end;
    averageHeight = y / end;

    while (i < items.length) heightMap[(i += 1)] = averageHeight;
    bottom = remaining * averageHeight;

    // prevent jumping if we scrolled up into unknown territory
    if (start < oldStart) {
      await tick();

      let expectedHeight = 0;
      let actualHeight = 0;

      for (let n = start; n < oldStart; n += 1) {
        if (rows[n - start]) {
          expectedHeight += heightMap[n];
          actualHeight += rows[n - start].offsetHeight;
        }
      }

      const d = actualHeight - expectedHeight;
      viewport.scrollTo(0, scrollTop + d);
    }

    // TODO if we overestimated the space these
    // rows would occupy we may need to add some
    // more. maybe we can just call handleScroll again?
  };

  // trigger initial refresh
  onMount(() => {
    rows = contents.getElementsByTagName('svelte-virtual-list-row') as HTMLCollectionOf<HTMLElement>;
    mounted = true;
  });

  export const scrollToTop = () => {
    viewport.scrollTo({ top: 0 });
  };
</script>

<svelte-virtual-list-viewport
  bind:this={viewport}
  bind:offsetHeight={viewportHeight}
  on:scroll={handleScroll}
  style="height: {height};"
>
  <svelte-virtual-list-contents
    bind:this={contents}
    style="padding-top: {top}px; padding-bottom: {bottom}px;"
  >
    {#each visible as row (row.data.id)}
      <svelte-virtual-list-row>
        <slot item={row.data}>Missing template</slot>
      </svelte-virtual-list-row>
    {/each}
  </svelte-virtual-list-contents>
</svelte-virtual-list-viewport>

<style>
  svelte-virtual-list-viewport {
    position: relative;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    display: block;
  }

  svelte-virtual-list-contents,
  svelte-virtual-list-row {
    display: block;
  }

  svelte-virtual-list-row {
    overflow: hidden;
  }
</style>
